/**
 * Customer support API types (migration 076). Serialized shapes — dates are ISO
 * strings, never `Date`.
 *
 * The customer and staff detail types are deliberately separate:
 * `SupportTicketDetail` cannot hold an internal note or an assignee, so leaking
 * one takes a deliberate cast rather than an omitted `.filter()`.
 */

export type SupportTicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_on_customer'
  | 'resolved'
  | 'closed';

export type SupportTicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export type SupportTicketCategory =
  | 'account'
  | 'billing'
  | 'jobs_matching'
  | 'auto_apply'
  | 'resume'
  | 'technical'
  | 'other';

export type SupportMessageAuthorType = 'customer' | 'employee' | 'system';

export type SupportMessageVisibility = 'public' | 'internal';

/** Statuses a ticket can still be worked in — everything but `closed`. */
export const SUPPORT_OPEN_STATUSES: SupportTicketStatus[] = [
  'open',
  'in_progress',
  'waiting_on_customer',
  'resolved',
];

/** Statuses the admin queue counts as outstanding work. */
export const SUPPORT_ACTIVE_STATUSES: SupportTicketStatus[] = ['open', 'in_progress'];

/** Most non-closed tickets one customer may hold at once. */
export const SUPPORT_MAX_OPEN_TICKETS_PER_CUSTOMER = 10;

export const SUPPORT_SUBJECT_MAX = 200;
export const SUPPORT_BODY_MAX = 5000;

/** What a `system` message records; `kind` drives how the thread renders it. */
export type SupportSystemMessageMetadata =
  | { kind: 'status_change'; from: SupportTicketStatus; to: SupportTicketStatus }
  | { kind: 'priority_change'; from: SupportTicketPriority; to: SupportTicketPriority }
  | { kind: 'assignment'; to: string | null; toName: string | null }
  | { kind: 'created' };

/** One entry in a ticket thread. */
export interface SupportMessage {
  id: string;
  ticketId: string;
  authorType: SupportMessageAuthorType;
  /** Customer or employee id; null for system rows. */
  authorId: string | null;
  /** Display name captured at write time. */
  authorName: string | null;
  body: string;
  visibility: SupportMessageVisibility;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/** Ticket header, shared by both surfaces. */
export interface SupportTicketSummary {
  id: string;
  /** Human-readable number, e.g. 'OUB-1042'. */
  reference: string;
  subject: string;
  category: SupportTicketCategory;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  /** Number of visible messages on the surface that produced this row. */
  messageCount: number;
}

// ---------------------------------------------------------------------------
// Customer surface
// ---------------------------------------------------------------------------

/** A row in the customer's own ticket list. */
export interface CustomerSupportTicket extends SupportTicketSummary {
  /** Public employee replies the customer has not read yet. */
  unreadCount: number;
  /** First line of the newest public message, for the list preview. */
  lastMessagePreview: string | null;
}

/** A ticket thread as the customer sees it — public messages only. */
export interface SupportTicketDetail extends CustomerSupportTicket {
  messages: SupportMessage[];
}

export interface CreateSupportTicketRequest {
  subject: string;
  category: SupportTicketCategory;
  body: string;
}

export interface CreateSupportMessageRequest {
  body: string;
}

export interface SupportTicketFilters {
  /** `active` collapses open + in_progress; the customer never sees the split. */
  status?: SupportTicketStatus | 'all' | 'active';
  page?: number;
  limit?: number;
}

export interface CustomerSupportSummary {
  /** Non-closed tickets. */
  openCount: number;
  /** Tickets carrying at least one unread public employee reply. */
  unreadCount: number;
}

// ---------------------------------------------------------------------------
// Staff surface
// ---------------------------------------------------------------------------

/** Who a ticket belongs to, resolved for the queue and the context rail. */
export interface SupportTicketCustomer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
}

/** A row in the admin queue. */
export interface AdminSupportTicket extends SupportTicketSummary {
  customer: SupportTicketCustomer;
  assignedTo: string | null;
  assignedToName: string | null;
  assignedAt: string | null;
  /** True when the newest public message came from the customer. */
  awaitingStaffReply: boolean;
  firstRespondedAt: string | null;
  lastMessagePreview: string | null;
}

/** A ticket thread as staff see it — includes internal notes. */
export interface SupportTicketAdminDetail extends AdminSupportTicket {
  messages: SupportMessage[];
  resolvedAt: string | null;
  closedAt: string | null;
  customerLastReadAt: string | null;
}

export interface AdminCreateSupportMessageRequest {
  body: string;
  visibility?: SupportMessageVisibility;
}

export interface UpdateSupportTicketRequest {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  /** `null` unassigns. Omit to leave assignment untouched. */
  assignedTo?: string | null;
}

export interface AdminSupportTicketFilters {
  status?: SupportTicketStatus | 'all' | 'active';
  priority?: SupportTicketPriority;
  category?: SupportTicketCategory;
  /** Employee id, or 'unassigned', or 'me'. */
  assignedTo?: string;
  /** Free text over reference, subject and customer name. */
  q?: string;
  page?: number;
  limit?: number;
}

export interface AdminSupportSummary {
  /** Active tickets whose newest public message came from the customer. */
  needsReply: number;
  /** Non-closed tickets with no assignee. */
  unassigned: number;
  /** Non-closed tickets assigned to the caller. */
  assignedToMe: number;
  openByPriority: Record<SupportTicketPriority, number>;
}

// ---------------------------------------------------------------------------
// List envelopes (match the API's nested-pagination shape)
// ---------------------------------------------------------------------------

export interface SupportPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CustomerSupportTicketsResponse {
  tickets: CustomerSupportTicket[];
  pagination: SupportPagination;
}

export interface AdminSupportTicketsResponse {
  tickets: AdminSupportTicket[];
  pagination: SupportPagination;
}
